import type { MouseEvent } from 'react'

/**
 * Scroll to an element by id (with or without a leading `#`).
 *
 * A missing element is a no-op ON PURPOSE, and two callers depend on it: an
 * anchor whose target is conditionally rendered, and the onboarding tracker's
 * links, which ask for their step's section on every press and get null on
 * every page but the one carrying it. That is how a `<Link>` whose destination
 * is the page you already occupy still does something — an unchanged location
 * fires no navigation, so the router never scrolls.
 *
 * Someone who asked for less motion is jumped there instead: the point is to
 * arrive at the section, and the animation is only how.
 */
export function scrollToHash(hash: string) {
  const id = hash.replace(/^#/, '')
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  document.getElementById(id)?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
}

/**
 * Click handler for `<a>` elements whose `href` may be an in-page hash.
 * Same-page hash links are intercepted and smooth-scrolled (the app has no
 * global `scroll-behavior: smooth`, so native anchor jumps are instant).
 * Non-hash hrefs (external links, routes) fall through to default behavior.
 */
export function handleHashAnchorClick(e: MouseEvent<HTMLAnchorElement>) {
  const href = e.currentTarget.getAttribute('href')
  if (href && href.length > 1 && href.startsWith('#')) {
    e.preventDefault()
    scrollToHash(href)
  }
}
